import { observable, action, computed } from "mobx"
import React from 'react'
import { getFileHash, getFileHashes, fixProgress, reTry, resetTimes } from "../tools/uploadTools"
import {
  signFile, initQingUpload, confirmUpload, initUpload, loadGroupList,
  createGroup, loadGroup, getBatchNumber, searchGroups
} from "../tools/uploadRequest"
import uploadChunks from "../tools/uploadChunks"
import Group from "./Group"
import File, { SUCCESS, EXCEPTION, ACTIVE, INACTIVE } from "./File"
import { message } from "antd"
import { getCurrentApp } from '../../../../../api/localTools'
export const TASK_TYPE = {
  file: "pgx_file",
  group: "pgx_group"
}

export default class TaskModel {
  @observable id = ""
  @observable batchNumber = ""
  @observable chunkPercent = 0
  @observable groups = []
  @observable searchedGroups = []
  @observable selectedGroupId
  @observable status = INACTIVE
  @observable waitingTime = -1
  interval = null
  currentGroup = null

  constructor (id, allowSuffix) {
    this.id = id
    this.allowSuffix = allowSuffix
  }

  @action loadBatchNumber = () => {
    getBatchNumber()
    .then(res =>
      this.batchNumber = res.batchNum
    , error =>
      message.error(error.message)
    )
  }

  /*
   * load groups of app
   */
  @action loadGroups = () => {
    loadGroupList(this.id)
    .then(result => {
      this.initGroups(result)
    }, error => {
      message.error(error.message)
    })
  }

  @action searchGroups = (keyword) => {
    searchGroups(this.id, keyword)
    .then(result => {
      this.searchedGroups = result.map(group => ({groupInfo: group, files: []}))
    }, error => {
      message.error(error.message)
    })
  }

  /*
   * load a group with groupId(familyId) when singleGroup
   */
  @action loadGroup = (groupId) => {
    loadGroup(groupId)
    .then(result => {
      this.initGroups([result])
      this.setSelectedId(result.id)
    })
  }

  @action initGroups = (result) => {
    result.forEach((groupInfo, index) => {
      this.groups.push(new Group(groupInfo))
    })
  }

  @action addGroupItem = (keyword) => {
    createGroup(keyword, this.id)
    .then(result => {
      this.unshiftGroup(result)
      this.setSelectedId(result.id)
    }, error => {
      message.error(error.message, 3)
    })
  }

  @action updateSelected = (groupInfo) => {
    this.setSelectedId(groupInfo.id)
    if (!this.selectedGroup) {
      this.unshiftGroup(groupInfo)
    }
  }

  @action unshiftGroup = (groupInfo) => {
    this.groups.unshift(new Group(groupInfo))
  }

  @action addFile = (file) => {
    this.selectedGroup.add(new File(file))
  }

  // 从上传列表中移除文件
  @action removeFile = (file) => {
    this.selectedGroup.remove(file)
  }

  @action removeTask = (task) => {
    return task.type === TASK_TYPE.file
      ? this.removeFileTask(task)
      : this.removeGroupTask(task)
  }

  @action removeTimer = () => {
    clearInterval(this.interval)
  }

  @computed get waiting () {
    return this.waitingTime !== -1
  }

  @action countDown = (duration, callback) => {
    this.waitingTime = duration
    this.interval = setInterval(() => {
      this.descendWaitingTime(callback)
    }, 1000)
  }

  @action descendWaitingTime = (callback) => {
    if (this.waitingTime > -1) {
      this.waitingTime--
    } else {
      this.removeTimer()
      callback()
    }
  }

  // 支持不同类型任务异常的分离处理
  // 进度列表中移除任务
  @action removeFileTask = task => {
    return new Promise((resolve, reject) => {
      const belongedGroup = this.groups.find(group => group.files.indexOf(task) > -1)
      if (task.isInactived || task.isError) {
        belongedGroup.remove(task)
        resolve("success")
      } else {
        reject("该任务状态已改变，无法删除")
      }
    })
  }

  @action addFileTask = (groupTask, file) => {
    return new Promise((resolve, reject) => {
      if (!groupTask.isSuccess) {
        groupTask.push(new File(file))
        resolve("success")
      } else {
        reject("该任务已完成，无法继续添加")
      }
    })
  }

  // 全部失败、小组未激活
  @action removeGroupTask = task => {
    const group = this.groups.find(group => group.groupInfo.name === task.name)
    return new Promise((resolve, reject) => {
      if (task.unReachable) {
        reject("该任务状态已改变，无法删除")
      } else {
        group.clear()
        resolve("success")
      }
    })
  }

  @action updateShortName = (index, value) => {
    if (this.selectedGroup) {
      this.selectedGroup.files[index].updateShortName(value)
    }
  }

  @action setSelectedId = (id) => {
    this.selectedGroupId = id
    if (!id) {
      return
    }
    const app = getCurrentApp()
    if (app === 'pgd') {
      this.selectedGroup.getJudgeData()
    }
  }

  @action checkDuplicate = (group, newFile) => {
    return group.files.some(fileModel => this.compare(newFile, fileModel.file))
  }

  compare = (file1, file2) => (
    file1.name === file2.name
    && file1.lastModified === file2.lastModified
    && file1.size === file2.size
  )

  @computed get preparedGroups () {
    return this.groups.filter(group => group.files.length !== 0)
  }

  /*
   * return currentGroup in groups
   * slice() 为了让 computed 跟踪 array
   */
  @computed get selectedGroup () {
    return this.groups.find(group => group.groupInfo.id === this.selectedGroupId)
  }

  @computed get isPending () {
    return this.status === ACTIVE
  }

  @computed get isSuccess () {
    return this.status === SUCCESS
  }

  @computed get isError () {
    return this.status === EXCEPTION
  }

  @computed get progress () {
    let progress = this.preparedGroups.reduce((sum, group) =>
      sum + group.progress * (group.chunkCount / this.chunkCount),
    0)
    return fixProgress(progress)
  }

  @computed get chunkCount () {
    return this.groups.reduce(
      (sum, group) => sum + group.chunkCount,
      0
    )
  }

  @computed get filesCount () {
    return this.groups.reduce(
    (sum, group) => sum + group.files.length,
    0)
  }

  @computed get failedCount () {
    return this.groups.reduce((sum, group) => sum + group.failedData.count, 0)
  }

  @computed get successCount () {
    return this.groups.reduce((sum, group) => sum + group.successCount, 0)
  }

  @computed get failedData () {
    return this.groups.filter(group => group.failedData.count > 0)
      .map(group => group.failedData)
  }

  @action startUpload () {
    resetTimes()
    return new Promise((resolve, reject) => {
      if (this.filesCount === 0) {
        reject("您还没有上传任何文件^_^")
      } else {
        this.status = ACTIVE
        this.currentGroup = this.preparedGroups[0]
        this.uploadFile(this.preparedGroups[0].files[0])
        resolve("开始上传")
      }
    })
  }

  @action uploadFile (file) {
    if (file.isSuccess) {
      this.uploadNextFile(file)
      return
    }

    file.setActive()

    let sign = {}, contentMd5 = "", md5Array = [], dataId = ""
    getFileHash(file.file).then(result => {
      contentMd5 = result
      return getFileHashes(file.file)
    }).then(result => { // file 为逻辑对象，file 字段为 dom 文件对象
      md5Array = result
      return signFile(file.name, contentMd5)
    }).then(result => {
      sign = result
      return initUpload(file, result.filename, contentMd5, this.id, this.batchNumber, this.currentGroup.referenceCount)
    }).then(result => {
      dataId = result.id
      return result.exist ? Promise.reject({isQuickUpload: true}) : initQingUpload(sign, contentMd5)
    }).then(qingRes => {
      resetTimes()
      uploadChunks(file.file, {...sign, contentMd5, md5Array}, qingRes,
        progress => {
          if (progress.isNormal) {
            file.updateProgress(fixProgress(progress.value))

            if (progress.value === 99) {
              this.confirmUpload(dataId, file)
            }
          } else {
            if (progress.value === 'bad_digest') {
              this.disableFile(file, '当前文件异常')
              message.error("当前文件异常，请重新选择上传")
            } else if (progress.value === "exception") {
              this.disableTask()
            }
          }
        }
      )
    }).catch(error => {
      if (error.isQuickUpload) {
        this.confirmUpload(dataId, file, true)
      } else {
        this.reUpload(file, error && error.message)
      }
    })
  }

  /**
   * 本步错误，青云端正确，服务端异常，则只需要重试本步骤即可。
   * @param dataId
   * @param file
   * @param isQuickUpload
   */
  confirmUpload (dataId, file, isQuickUpload = false) {
    confirmUpload(dataId)
    .then(() => {
        file.updateProgress(100)
        file.setSuccess()
        if (isQuickUpload) {
          file.setQuickUpload()
        }
        this.uploadNextFile(file)
      }, error => {
        this.reConfirmUpload(dataId, file, isQuickUpload, error && error.message)
      }
    )
  }

  reConfirmUpload (dataId, file, isQuickUpload = false, message) {
    reTry(() => this.confirmUpload(dataId, file, isQuickUpload), message)
      .catch(() => this.disableFile(file))
  }

  reUpload (file, message) {
    reTry(() => this.uploadFile(file), message)
    .then(null, this.disableTask)
  }

  disableTask = () => {
    this.status = EXCEPTION
    this.groups.forEach(group => {
      group.files.forEach(file => {
        file.isActived && file.setError('网络异常，请检查网络')
      })
    })
  }

  disableFile = (file, message) => {
    file.setError(message)
    this.uploadNextFile(file)
  }

  @action uploadNextFile (file) {
    const nextFile = this.getNextFile(file, this.preparedGroups)
    if (!nextFile || !this.isPending) {
      return
    } else {
      this.uploadFile(nextFile)
    }
  }

  @action getNextFile (file, groups) {
    const currentFiles = this.currentGroup.files
    const currentFileIndex = currentFiles.indexOf(file)
    const currentGroupIndex = groups.indexOf(this.currentGroup)

    if (currentGroupIndex < groups.length) {
      if (currentFileIndex + 1 <currentFiles.length) {
        return currentFiles[currentFileIndex + 1]
      } else if (currentGroupIndex === groups.length - 1) {
        this.status = SUCCESS
      } else {
        this.currentGroup = groups[currentGroupIndex + 1] // 更新当前激活的组
        return groups[currentGroupIndex + 1].files[0]
      }
    }
  }
}

const AZF_Miseq = '302'
export class AzfTaskModel extends TaskModel {
  constructor (id, allowSuffix) {
    super(id, allowSuffix)
    this.isMiseq = id === AZF_Miseq
  }

  @action sortFiles = () => {
    this.preparedGroups.forEach(group => {
      const references = [], rest = []
      group.files.forEach(file => {
        if (file.reference) {
          references.push(file)
        } else {
          rest.push(file)
        }
      })
      if (references.length > 0) {
        group.files = references.concat(rest)
      }
    })
  }

  validateGroup = () => {
    let referenceMessage = [],
        fileMessage = []

    this.preparedGroups.forEach(group => {
      const referenceLength = group.files.filter(file => file.reference).length
      if (referenceLength <= 1) {
        referenceMessage.push(<span><br />{group.groupInfo.name}</span>)
      }
      if (group.files.length === 1) {
        fileMessage.push(<span><br />{group.groupInfo.name}</span>)
      }
    })
    let msg = <span>{fileMessage.length > 0 && '以下组至少上传两个文件'}{fileMessage.length > 0 && fileMessage}<br />
      {referenceMessage.length > 0 && '以下组至少选择两个参照'}{referenceMessage.length > 0 && referenceMessage}</span>
    if (referenceMessage.length > 0 || fileMessage.length > 0) {
      throw msg
    }
  }

  // 有关继承，只能用 function
  @action async startUpload () {
    this.isMiseq && this.validateGroup()
    this.sortFiles()
    return await super.startUpload()
  }
}



// WEBPACK FOOTER //
// ./src/app/components/normal/BasicModal/UploadModal/stores/TaskModel.js